import { apiClient } from '@/lib/api/client';
import { setPrimaryToken, setTemporaryToken } from '@/lib/auth/storage';
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from '@/lib/storage/web-storage';

export type TemporarySignupResponse = {
  status: string;
  token?: string;
  message?: string;
};

export type UploadResumeResponse = {
  status?: string;
  message?: string;
  file_id?: number;
  id?: number;
  data?: {
    file_id?: number;
  };
};

export type ExtractResumeResponse = {
  status?: string;
  message?: string;
  file_contents?: string;
  text?: string;
  data?: {
    text?: string;
  };
};

export type ImportCreateBody = {
  file_id?: number;
  resume_text?: string;
  type: 'import_only';
  target_format: string;
  check_content_length?: boolean;
};

export type ImportCreateResponse = {
  status?: string;
  message?: string;
  request_id?: number;
  data?: {
    request_id?: number;
  };
};

export type ImportStatusResponse = {
  status?: string;
  message?: string;
  request_status?: string;
  resume_id?: number;
  data?: {
    resume_id?: number;
  };
  import_status?: {
    resume_id?: number;
  };
};

export async function ensureFormatCheckerSession(): Promise<string> {
  if (typeof window === 'undefined') {
    return '';
  }

  const existing =
    getLocalStorageItem('temporary_token') || getLocalStorageItem('tokensz');
  if (existing) {
    return existing;
  }

  const { data } = await apiClient.post<TemporarySignupResponse>(
    '/pro/auth/temporary-signup',
    {},
    { timeout: 10000 },
  );

  if (data.status === 'success' && data.token) {
    setTemporaryToken(data.token);
    setPrimaryToken(data.token);
    return data.token;
  }

  throw new Error(data.message || 'Unable to create session');
}

export async function uploadResumeFile(file: File): Promise<number> {
  const formData = new FormData();
  formData.append('file', file);

  const { data } = await apiClient.post<UploadResumeResponse>(
    '/pro/resume/upload',
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    },
  );

  const fileId =
    data.file_id || data.id || data.data?.file_id;

  if (!fileId) {
    throw new Error(data.message || 'Failed to upload resume file');
  }

  return fileId;
}

export async function extractResumeText(file: File): Promise<string> {
  const formData = new FormData();
  formData.append('file', file);

  const { data } = await apiClient.post<ExtractResumeResponse>(
    '/pro/resume/extract',
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    },
  );

  const text = data.file_contents || data.text || data.data?.text;
  if (!text) {
    throw new Error(data.message || 'No text extracted');
  }

  return text;
}

export async function createImportRequest(
  body: ImportCreateBody,
): Promise<number> {
  const { data } = await apiClient.post<ImportCreateResponse>(
    '/pro/resume/import/create',
    body,
  );

  const requestId = data.request_id || data.data?.request_id;

  if (!requestId) {
    throw new Error(data.message || 'Import request ID not returned');
  }

  return requestId;
}

export async function checkImportStatus(
  requestId: number,
): Promise<ImportStatusResponse> {
  const { data } = await apiClient.post<ImportStatusResponse>(
    '/pro/resume/import/status',
    { request_id: Number(requestId) },
  );
  return data;
}
